import React, { useState, useEffect, useCallback } from 'react';
import { AdminAuthService } from '../services/AdminAuthService';
import { AdminLogin } from './AdminLogin';
import { AdminSidebar } from './AdminSidebar';
import { OverviewDashboard } from './OverviewDashboard';
import { GameManagement } from './GameManagement';
import { UserAnalytics } from './UserAnalytics';
import { GameAnalytics } from './GameAnalytics';
import { CostExplorer } from './CostExplorer';
import { SystemHealth } from './SystemHealth';
import { UserManagement } from './UserManagement';
import { AdminSection } from '../types';
import './AdminApp.css';

export const AdminApp: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeSection, setActiveSection] = useState<AdminSection>('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const authService = AdminAuthService.getInstance();

  useEffect(() => {
    setIsAuthenticated(authService.isAuthenticated());
  }, [authService]);

  // Refresh session on activity
  useEffect(() => {
    if (isAuthenticated) {
      authService.refreshSession();
    }
  }, [activeSection, isAuthenticated, authService]);

  const handleLoginSuccess = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  const handleLogout = useCallback(() => {
    authService.logout();
    setIsAuthenticated(false);
  }, [authService]);

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return <OverviewDashboard key={refreshKey} />;
      case 'games':
        return <GameManagement key={refreshKey} />;
      case 'users':
        return <UserAnalytics key={refreshKey} />;
      case 'user-management':
        return <UserManagement key={refreshKey} />;
      case 'analytics':
        return <GameAnalytics key={refreshKey} />;
      case 'costs':
        return <CostExplorer key={refreshKey} />;
      case 'system':
        return <SystemHealth key={refreshKey} />;
      default:
        return <OverviewDashboard key={refreshKey} />;
    }
  };

  const sectionTitles: Record<AdminSection, string> = {
    overview: 'Overview',
    games: 'Games',
    users: 'Users',
    'user-management': 'User Management',
    analytics: 'Analytics',
    costs: 'Costs',
    system: 'System',
  };

  return (
    <div className="admin-app">
      <header className="admin-app__header">
        <div className="admin-app__header-left">
          <button
            className="admin-app__menu-btn"
            onClick={() => setSidebarCollapsed(c => !c)}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <h1 className="admin-app__brand">
            <span className="admin-app__brand-icon">🛡️</span>
            PlayHub Admin
          </h1>
          <span className="admin-app__section-badge">
            {sectionTitles[activeSection]}
          </span>
        </div>
        <div className="admin-app__header-right">
          <button
            className="admin-app__refresh-btn"
            onClick={handleRefresh}
            title="Refresh data"
            aria-label="Refresh data"
          >
            🔄
          </button>
          <button
            className="admin-app__logout-btn"
            onClick={handleLogout}
            title="Logout"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="admin-app__body">
        <AdminSidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        />

        {/* Overlay for mobile sidebar */}
        {!sidebarCollapsed && (
          <div
            className="admin-app__overlay"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        <main className="admin-app__content">
          {renderSection()}
        </main>
      </div>
    </div>
  );
};
